import React from 'react';
import Icon from '../../../components/AppIcon';
import { motion } from 'framer-motion';

const EquifaxDisclaimersSection = ({ currentLanguage }) => {
  const content = {
    EN: {
      title: "Mandatory Equifax® Disclaimers",
      disclaimers: [
        {
          icon: "AlertTriangle",
          title: "Credit Scores Are Informational",
          text: "Credit scores provided are for informational purposes only and may not be the same score used by lenders or other financial institutions."
        },
        {
          icon: "BarChart3",
          title: "Multiple Score Versions Exist",
          text: "There are multiple versions of credit scores, and lenders may use different scoring models. The score you see may differ from what lenders see."
        },
        {
          icon: "XCircle",
          title: "No Guaranteed Improvement",
          text: "PSPXpress cannot guarantee credit score improvement. Credit building results depend on multiple factors and vary by individual."
        },
        {
          icon: "TrendingDown",
          title: "Opening/Closing Accounts May Lower Scores",
          text: "Opening or closing credit accounts may initially lower your credit scores. This is a normal part of credit activity."
        },
        {
          icon: "Clock",
          title: "Reporting Timeframes",
          text: "Credit reporting may take 30-60 days to appear on your credit report. Not all activity may be reported to credit bureaus."
        },
        {
          icon: "Shield",
          title: "Equifax® Trademark Notice",
          text: "Equifax® is a registered trademark of Equifax Inc. PSPXpress is not affiliated with or endorsed by Equifax Inc."
        }
      ],
      important: "Important",
      notice: "These disclaimers are required by Equifax® and apply to all credit-related services provided by PSPXpress."
    },
    FR: {
      title: "Avis de non-responsabilité obligatoires Equifax®",
      disclaimers: [
        {
          icon: "AlertTriangle",
          title: "Les scores de crédit sont informationnels",
          text: "Les scores de crédit fournis sont à des fins informationnelles seulement et peuvent ne pas être le même score utilisé par les prêteurs ou autres institutions financières."
        },
        {
          icon: "BarChart3",
          title: "Plusieurs versions de scores existent",
          text: "Il existe plusieurs versions de scores de crédit, et les prêteurs peuvent utiliser différents modèles de notation. Le score que vous voyez peut différer de ce que voient les prêteurs."
        },
        {
          icon: "XCircle",
          title: "Aucune amélioration garantie",
          text: "PSPXpress ne peut garantir l\'amélioration du score de crédit. Les résultats de construction de crédit dépendent de multiples facteurs et varient selon l\'individu."
        },
        {
          icon: "TrendingDown",
          title: "Ouvrir/fermer des comptes peut réduire les scores",
          text: "Ouvrir ou fermer des comptes de crédit peut initialement réduire vos scores de crédit. Ceci fait partie normale de l\'activité de crédit."
        },
        {
          icon: "Clock",
          title: "Délais de rapport",
          text: "Le rapport de crédit peut prendre 30-60 jours pour apparaître sur votre rapport de crédit. Toute activité ne peut pas être rapportée aux bureaux de crédit."
        },
        {
          icon: "Shield",
          title: "Avis de marque Equifax®",
          text: "Equifax® est une marque déposée d'Equifax Inc. PSPXpress n'est pas affilié ou approuvé par Equifax Inc."
        }
      ],
      important: "Important",
      notice: "Ces avertissements sont requis par Equifax® et s'appliquent à tous les services liés au crédit fournis par PSPXpress."
    }
  };

  const text = content?.[currentLanguage] || content?.EN;

  return (
    <section className="py-16 bg-amber-50 border-y-2 border-amber-300">
      <div className="container mx-auto px-6 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 bg-amber-200 rounded-full px-4 py-2 mb-4">
            <Icon name="AlertCircle" size={20} className="text-amber-800" />
            <span className="font-semibold text-amber-800">{text?.important}</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-4">
            {text?.title}
          </h2>
          <p className="text-amber-800 max-w-2xl mx-auto">
            {text?.notice}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {text?.disclaimers?.map((disclaimer, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-6 border-2 border-amber-200 shadow-sm"
            >
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <Icon name={disclaimer?.icon} size={20} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-900 mb-2">
                    {disclaimer?.title}
                  </h3>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    {disclaimer?.text}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Legal Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-12 p-6 bg-amber-100 rounded-lg border border-amber-300"
        >
          <div className="text-center">
            <p className="text-xs text-amber-800 leading-relaxed">
              {currentLanguage === 'FR' ?'Les informations de crédit sont fournies par Equifax Canada Co. Les scores de crédit peuvent varier et ne sont pas garantis d\'être exacts ou à jour. Consultez toujours un conseiller financier professionnel pour des conseils personnalisés.' :'Credit information is provided by Equifax Canada Co. Credit scores may vary and are not guaranteed to be accurate or up-to-date. Always consult a professional financial advisor for personalized advice.'
              }
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default EquifaxDisclaimersSection;